import { TFunction } from '@frontend/i18n';
import { DynamicFormStoreState } from '../store';
import { getFieldAttachments, getFieldUploadStatus } from './selectors';
import { MediaValidationRules } from './types';

interface MediaFieldValidatorArgs {
  fieldKey: string;
  rules?: MediaValidationRules;
  /** Translation function scoped to the `digital-forms-experience` namespace. */
  t: TFunction;
  /** Live, non-reactive read of the DynamicFormStore attachments slice. */
  getState: () => Pick<DynamicFormStoreState, 'attachments'>;
}

/**
 * Builds a synchronous validator for a media field, compatible with TanStack Form field
 * validators (`onChange` / `onSubmit`). Returns an error string that blocks section advance
 * while any attachment is still uploading, any attachment failed, or required/min-count rules
 * are unmet. Returns `undefined` when the field is valid.
 *
 * The validator reads the DynamicFormStore directly (not the field value) so async upload
 * state is respected when `form.handleSubmit()` runs at the "Next" boundary.
 */
export const getMediaFieldValidator =
  ({ fieldKey, rules = {}, t, getState }: MediaFieldValidatorArgs) =>
  (): string | undefined => {
    const attachments = getState().attachments;
    const status = getFieldUploadStatus(attachments, fieldKey);

    if (status.isUploading) {
      return t('Please wait for uploads to finish', { ns: 'digital-forms-experience' });
    }

    if (status.hasError) {
      return t('Please resolve the file upload errors', { ns: 'digital-forms-experience' });
    }

    const successCount = getFieldAttachments(attachments, fieldKey).filter(
      (attachment) => attachment.status === 'success'
    ).length;

    if (rules.required && successCount === 0) {
      return t('Please attach the required image(s)', { ns: 'digital-forms-experience' });
    }

    if (rules.minFiles && successCount < rules.minFiles) {
      return t('Please upload at least {{count}} file(s)', { count: rules.minFiles, ns: 'digital-forms-experience' });
    }

    return undefined;
  };
